import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Loader2, CheckCircle2, Mail, Printer, ArrowRight, AlertTriangle } from "lucide-react";
import PricingCard from "../../components/wealth/PricingCard";
import ToolkitDownloads from "../../components/wealth/ToolkitDownloads";
import { PRODUCT_NAME } from "../../lib/wealthPricing";
import { trackClick, annuityReviewUrl } from "../../lib/crossSell";

const TIER_COLOR = {
    Strong: "#047857",
    Solid: "#0284c7",
    Caution: "#d97706",
    Critical: "#b91c1c",
};
const SUB_LABELS = {
    income: "Income Confidence",
    growth: "Growth Strength",
    protection: "Market Protection",
    flexibility: "Flexibility & LTC",
};

export default function WealthResults() {
    const [params] = useSearchParams();
    const location = useLocation();
    const sessionId = params.get("session_id");
    const queryLeadId = params.get("lead_id");
    // The Toolkit-completion flow navigates here with score in route state
    // (no Stripe polling needed since /complete-from-toolkit returns it inline).
    const stateScore = location.state?.score;
    const stateLeadId = location.state?.leadId;
    const [status, setStatus] = useState(stateScore ? "paid" : (queryLeadId ? "fetching" : "polling"));
    const [score, setScore] = useState(stateScore || null);
    const [leadId, setLeadId] = useState(stateLeadId || queryLeadId || null);
    const [isPurchased, setIsPurchased] = useState(Boolean(stateScore));   // toolkit unlock state
    const [upsellWorking, setUpsellWorking] = useState(false);
    const [attempts, setAttempts] = useState(0);

    const poll = useCallback(
        async (n) => {
            if (n >= 10) {
                setStatus("error");
                return;
            }
            try {
                const { data } = await api.get(`/wealth/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setScore(data.score);
                    setLeadId(data.lead_id);
                    setIsPurchased(true);
                    setStatus("paid");
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
                setAttempts(n + 1);
                setTimeout(() => poll(n + 1), 2000);
            } catch (e) {
                if (e?.response?.status === 404) {
                    setStatus("error");
                    return;
                }
                setAttempts(n + 1);
                setTimeout(() => poll(n + 1), 2500);
            }
        },
        [sessionId],
    );

    useEffect(() => {
        if (stateScore) return; // already have it from Toolkit completion
        if (queryLeadId && !sessionId) {
            // FREE FUNNEL · direct results lookup. Use the un-gated endpoint;
            // /free-result returns score regardless of paid status.
            (async () => {
                try {
                    const { data } = await api.get(`/wealth/free-result/${queryLeadId}`);
                    setScore(data.wealth_report_score);
                    setLeadId(data.id);
                    setIsPurchased(Boolean(data.wealth_report_purchased));
                    setStatus("paid");
                } catch {
                    setStatus("error");
                }
            })();
            return;
        }
        if (!sessionId) {
            setStatus("error");
            return;
        }
        poll(0);
    }, [sessionId, queryLeadId, stateScore, poll]);

    if (status === "polling" || status === "fetching") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="wealth-results-polling">
                    <Loader2 className="w-10 h-10 text-emerald-700 animate-spin mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl text-emerald-950 font-semibold">{status === "fetching" ? "Loading your scorecard…" : "Finalizing your report…"}</h2>
                    <p className="mt-3 text-sm text-emerald-900">{status === "fetching" ? "Pulling your saved Retirement Readiness Scorecard™." : "Confirming payment and calculating your score. This should take just a few seconds."}</p>
                    {status === "polling" && <div className="mt-4 text-xs text-emerald-800">Attempt {attempts + 1}</div>}
                </div>
            </div>
        );
    }

    if (status === "expired" || status === "error") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="wealth-results-error">
                    <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl text-emerald-950 font-semibold">
                        {status === "expired" ? "Checkout session expired" : "We hit a snag"}
                    </h2>
                    <p className="mt-3 text-sm text-emerald-900">
                        {status === "expired"
                            ? "Your secure checkout link timed out before payment was completed. No charge was made."
                            : "We couldn't confirm your payment status. If you were charged, your report will arrive by email shortly — otherwise no charge was made."}
                    </p>
                    <Link to="/wealth/assessment" className="mt-6 inline-flex items-center gap-2 bg-emerald-900 text-amber-300 px-5 py-3 rounded-lg font-medium text-sm">Try again →</Link>
                </div>
            </div>
        );
    }

    // PAID
    const tierColor = TIER_COLOR[score?.tier] || "#047857";

    return (
        <div className="bg-[#fdfbf6] py-16 px-4 print:bg-white" data-testid="wealth-results">
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-10">
                    {isPurchased ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold tracking-wide">
                            <CheckCircle2 className="w-3.5 h-3.5" /> PAYMENT CONFIRMED · REPORT EMAILED
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold tracking-wide">
                            <CheckCircle2 className="w-3.5 h-3.5" /> FREE ASSESSMENT COMPLETE · SCORE EMAILED
                        </div>
                    )}
                    <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-emerald-950 font-semibold leading-tight">Your {PRODUCT_NAME}</h1>
                </div>

                {isPurchased && <PricingCard variant="receipt" className="mb-8" />}

                <div className="bg-white rounded-2xl border border-emerald-900/10 shadow-xl shadow-emerald-900/5 p-10 sm:p-14" data-testid="score-card">
                    {/* Score */}
                    <div className="text-center">
                        <div
                            className="mx-auto w-44 h-44 rounded-full border-[7px] flex items-center justify-center font-serif font-bold"
                            style={{ borderColor: tierColor, color: tierColor }}
                        >
                            <span className="text-7xl leading-none">{score?.overall ?? "—"}</span>
                        </div>
                        <div className="mt-5 text-[11px] tracking-[0.2em] uppercase font-bold" style={{ color: tierColor }} data-testid="score-tier">
                            Overall · {score?.tier}
                        </div>
                        <p className="mt-5 max-w-xl mx-auto text-base text-emerald-900/80 leading-relaxed">{score?.headline}</p>
                    </div>

                    {/* Sub-scores */}
                    <div className="mt-10 pt-8 border-t border-emerald-900/10 space-y-5" data-testid="sub-scores">
                        <div className="text-xs uppercase tracking-[0.18em] text-emerald-900 font-bold">Sub-scores</div>
                        {score?.sub_scores && Object.entries(score.sub_scores).map(([k, v]) => (
                            <div key={k}>
                                <div className="flex justify-between text-sm font-medium">
                                    <span className="text-emerald-900/80">{SUB_LABELS[k] || k}</span>
                                    <span className="text-emerald-950">{v}/25</span>
                                </div>
                                <div className="mt-1.5 h-2 bg-emerald-900/8 rounded-full overflow-hidden">
                                    <div className="h-full" style={{ width: `${(v / 25) * 100}%`, background: tierColor }} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Recommendations */}
                    <div className="mt-10 p-6 rounded-xl bg-[#fdfbf6] border-l-4" style={{ borderLeftColor: tierColor }} data-testid="recommendations">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: tierColor }}>What to do next</div>
                        <ul className="space-y-3 text-sm text-emerald-950 leading-relaxed">
                            {score?.recommendations?.map((r, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-900 text-amber-300 text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                                    <span>{r}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Email + Print */}
                    <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-center text-sm">
                        <div className="inline-flex items-center gap-2 text-emerald-900" data-testid="emailed-note">
                            <Mail className="w-4 h-4" /> A printable PDF-style version is in your inbox now.
                        </div>
                        <button
                            onClick={() => window.print()}
                            data-testid="print-button"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-emerald-900/15 hover:bg-emerald-50 text-emerald-900 text-xs font-medium"
                        >
                            <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
                        </button>
                    </div>
                </div>

                {/* Toolkit Downloads OR Upsell — depends on purchase state */}
                {isPurchased ? (
                    <>
                        <ToolkitDownloads leadId={leadId} />
                        {leadId && (
                            <div className="mt-6 text-center" data-testid="library-link-row">
                                <Link
                                    to={`/wealth/library?lead_id=${leadId}`}
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-900 hover:text-emerald-700 underline underline-offset-2 decoration-emerald-300"
                                    data-testid="results-library-link"
                                >
                                    Open my full Toolkit Library <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="mt-2 p-7 sm:p-10 rounded-2xl border-2 border-emerald-900/15 bg-white shadow-xl" data-testid="results-toolkit-upsell">
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold tracking-wide">
                                OPTIONAL · 12 RESOURCES · $549 VALUE
                            </div>
                            <h3 className="mt-5 font-serif text-3xl sm:text-4xl text-emerald-950 font-semibold leading-tight">
                                Go deeper with the full Retirement Readiness Toolkit
                            </h3>
                            <p className="mt-4 text-base text-emerald-900 max-w-2xl mx-auto leading-relaxed">
                                Your free score above tells you where you stand. The Toolkit gives you <strong>10 expert-built PDFs</strong> covering Social Security, taxes, RMDs, Medicare, estate planning, document organization, rollovers, annuities and Gold &amp; Silver IRAs — plus an <strong>optional 15-minute consultation</strong> with a licensed retirement pro (no sales, just a second opinion) — so you can <strong>actually close the gaps</strong>.
                            </p>
                        </div>
                        <ul className="mt-7 max-w-2xl mx-auto space-y-2.5 text-sm text-emerald-950">
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Retirement Income Gap Worksheet</strong> — calculate your exact monthly shortfall <span className="text-amber-700 font-bold">($39)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Social Security Maximization Guide</strong> — when to claim, spousal &amp; survivor strategy <span className="text-amber-700 font-bold">($39)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Retirement Tax Reduction Checklist</strong> — line-by-line audit <span className="text-amber-700 font-bold">($39)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>RMD &amp; Withdrawal Sequencing Planner</strong> — minimize lifetime tax <span className="text-amber-700 font-bold">($39)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Medicare &amp; Healthcare Cost Planner</strong> — IRMAA, Medigap vs. Advantage, long-term care <span className="text-amber-700 font-bold">($39)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Estate &amp; Legacy Planning Checklist</strong> — beneficiary audit + SECURE Act traps <span className="text-amber-700 font-bold">($29)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Retirement Document Organizer</strong> — folder system your family needs <span className="text-amber-700 font-bold">($29)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>IRA &amp; 401(k) Rollover Checklist</strong> — move assets tax-free <span className="text-amber-700 font-bold">($29)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Gold &amp; Silver IRA Guide</strong> — buyer-protection playbook <span className="text-amber-700 font-bold">($35)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Annuity Owner's Guide</strong> — audit any contract in plain English <span className="text-amber-700 font-bold">($49)</span></span></li>
                            <li className="flex items-start gap-2"><span className="mt-1 text-emerald-700">✓</span><span><strong>Optional 15-min Consultation</strong> — complimentary second opinion with a licensed pro, no sales <span className="text-amber-700 font-bold">($99)</span></span></li>
                        </ul>
                        <div className="mt-8 text-center">
                            <button
                                onClick={async () => {
                                    if (upsellWorking) return;
                                    setUpsellWorking(true);
                                    try {
                                        const { data } = await api.post(`/wealth/start-from-lead`, {
                                            lead_id: leadId,
                                            origin_url: window.location.origin,
                                        });
                                        if (data?.checkout_url) {
                                            window.location.assign(data.checkout_url);
                                        } else {
                                            throw new Error("No checkout URL");
                                        }
                                    } catch {
                                        setUpsellWorking(false);
                                    }
                                }}
                                disabled={upsellWorking}
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-emerald-900 hover:bg-emerald-800 text-amber-300 font-bold text-base shadow-lg disabled:opacity-60 transition-all"
                                data-testid="results-upsell-cta"
                            >
                                {upsellWorking ? "Loading checkout…" : "Unlock the full Toolkit for $29.95 →"}
                            </button>
                            <p className="mt-4 text-xs text-emerald-900">Secure Stripe checkout · Instant access · 100% refund if you're not impressed</p>
                        </div>
                    </div>
                )}

                {/* Other free retirement resources — neutral framing, annuity review
                    is one of several optional next steps, not the headline. */}
                <div className="mt-8 p-7 sm:p-8 rounded-2xl bg-white border border-emerald-900/15" data-testid="cross-sell">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-bold">Other free retirement resources</div>
                    <h3 className="mt-3 font-serif text-xl sm:text-2xl text-emerald-950 font-semibold leading-tight">
                        Looking for a deeper look at one part of your plan?
                    </h3>
                    <p className="mt-3 text-sm text-emerald-900/80 leading-relaxed max-w-2xl">
                        Your Retirement Readiness Toolkit covers the broad picture. If you'd like a one-on-one conversation focused on a specific piece — your annuity contract, your income plan, or your overall portfolio — request a complimentary review below. No pressure, no follow-up sales calls.
                    </p>
                    <a
                        href={annuityReviewUrl({ leadId })}
                        onClick={() => trackClick("annuity_review", { source_page: "wealth_results", lead_id: leadId })}
                        data-testid="cross-sell-cta"
                        className="mt-5 inline-flex items-center gap-2 bg-white hover:bg-emerald-50 text-emerald-900 border-2 border-emerald-900 px-5 py-2.5 rounded-md font-semibold text-sm transition-colors"
                    >
                        Request a complimentary review <ArrowRight className="w-4 h-4" />
                    </a>
                </div>

                {/* Compliance disclaimer */}
                <p className="mt-8 text-xs text-emerald-800 leading-relaxed" data-testid="results-disclaimer">
                    <strong>Disclosure:</strong> Educational and informational purposes only. Not financial, tax, legal, investment, or insurance advice. Consult a licensed professional for advice tailored to your individual circumstances.
                </p>
            </div>
        </div>
    );
}
